/**
 * Hook per leggere i contributi di UI che i plugin abilitati montano in uno
 * slot di una schermata core (vedi @crm/contracts/SlotContributor).
 *
 * Stesso filtering di useActionContributors/useSidebarContributors:
 *   - plugin autorizzati dal backend (user.plugins)
 *   - plugin non disabilitati via toggle localStorage
 *   - contributor con entityType E slot corrispondenti
 *
 * In più ordina per `order`. L'ordinamento sta qui e non nei call site perché
 * gli slot senza contenuto core — es. 'detail-left:after' — renderizzano il
 * risultato così com'è: ordinare solo dove serve il merge lascerebbe quegli
 * slot in ordine di registrazione.
 *
 * `Array.prototype.sort` è stabile (ES2019+), quindi un call site che fonde
 * questo risultato con i propri campi core e ri-ordina non perde l'ordine
 * relativo dei contributi a parità di `order`.
 */

import { useMemo } from 'react';
import type {
  BusinessSlot,
  EntityDetailSlot,
  MessageSlot,
  QuotationSlot,
  SettingsSlot,
  SlotContributor,
} from '@crm/contracts';
import { useEnabledPlugins } from './useEnabledPlugins';

/**
 * Peso di un contributo senza `order`: va in coda, dopo qualunque elemento
 * ordinato.
 *
 * Non è un numero grande a caso ed è deliberatamente non un valore "alto ma
 * plausibile" tipo 1000: quello presupporrebbe di sapere fin dove arriva la
 * numerazione di chi fonde i propri elementi con questi. `MAX_SAFE_INTEGER`
 * significa "ultimo" senza sapere nulla di come sono numerati gli altri.
 *
 * Esportato perché i call site che fondono contributi e contenuto proprio
 * devono usare lo stesso peso, altrimenti l'ordine cambia a seconda di chi
 * ordina.
 */
export const SLOT_ORDER_LAST = Number.MAX_SAFE_INTEGER;

export function useSlotContributors(
  entityType: string,
  slot: QuotationSlot | MessageSlot | EntityDetailSlot | SettingsSlot | BusinessSlot,
): SlotContributor[] {
  const enabledPlugins = useEnabledPlugins();
  return useMemo(
    () =>
      enabledPlugins
        .flatMap((p) =>
          (p.slotContributors ?? []).filter(
            (c) => c.entityType === entityType && c.slot === slot,
          ),
        )
        .sort((a, b) => (a.order ?? SLOT_ORDER_LAST) - (b.order ?? SLOT_ORDER_LAST)),
    [enabledPlugins, entityType, slot],
  );
}
